// ANALYSIS AND CHANGES:
// This is a new component that wraps the application modal and the thank-you message.
// RESPONSABILIDADES:
// 1. Receive all of its props and state from the `useApplicationForm` hook, making it a "dumb UI" component.
// 2. Show the details of the position (`position`) the user is applying to.
// 3. Render the application form with the required fields.
// 4. Conditionally render the "Thank you" screen after a successful submit.
// BENEFITS:
// - Fully decouples the form UI from the logic of the main page.
// - Lets the modal be reused or changed without affecting `CareersPage`.

import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Typography,
  Upload,
  Divider,
} from "antd";
import {
  CheckCircleOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Position } from "@common";
import { InputMask } from "@components/atoms";
import { FILE_ACCEPT_STRING } from "@helpers";
import { UploadFile } from "antd/es/upload";

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

interface JobApplicationModalProps {
  isOpen: boolean;
  onCancel: () => void;
  position: Position | null;
  formProps: {
    form: any;
    onFinish: (values: any) => void;
    handleResumeUpload: (file: UploadFile) => boolean;
    isLoading: boolean;
    resumeFile: File | null;
  };
  isThankYouVisible: boolean;
  onCloseThankYou: () => void;
}

export const JobApplicationModal = ({
  isOpen,
  onCancel,
  position,
  formProps,
  isThankYouVisible,
  onCloseThankYou,
}: JobApplicationModalProps) => {
  
  const renderApplicationForm = () => (
    <>
      <Title level={4} className="!font-azonix !text-primary text-center">
        Apply for: {position?.title}
      </Title>
      <Paragraph className="text-center text-secondary mb-6">
        {position?.location} | {position?.type}
      </Paragraph>
      <Divider className="!bg-rock-grey" />
      <Form
        form={formProps.form}
        layout="vertical"
        onFinish={formProps.onFinish}
        requiredMark={false}
        disabled={formProps.isLoading}
      >
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="FULL NAME"
              name="fullName"
              rules={[{ required: true, message: "Please enter your full name" }]}
            >
              <Input placeholder="Your full name" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="EMAIL"
              name="email"
              rules={[
                { required: true, message: "Please enter your email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input placeholder="Your email" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label="PHONE"
          name="phone"
          rules={[{ required: true, message: "Please enter your phone number" }]}
        >
          <InputMask mask="usphone" placeholder="Your phone number" />
        </Form.Item>
        <Form.Item
          label="TELL US ABOUT YOURSELF"
          name="message"
          rules={[
            {
              required: true,
              message: "Please tell us why you want to work with us",
            },
          ]}
        >
          <TextArea rows={4} placeholder="Your message" />
        </Form.Item>
        <Form.Item
          label="ATTACH RESUME"
          name="resume"
          rules={[{ required: true, message: "Please upload your resume" }]}
          help={formProps.resumeFile ? `File attached: ${formProps.resumeFile.name}` : "PDF, DOC, DOCX up to 5MB"}
        >
          <Upload
            name="resume"
            listType="text"
            accept={FILE_ACCEPT_STRING}
            beforeUpload={formProps.handleResumeUpload}
            maxCount={1}
            showUploadList={false}
          >
            <Button icon={<UploadOutlined />}>CHOOSE FILE</Button>
          </Upload>
        </Form.Item>
      </Form>
    </>
  );

  const renderThankYouMessage = () => (
     <div className="text-center p-4">
        <CheckCircleOutlined className="text-primary text-6xl mb-6" />
        <Title level={3} className="!text-white mb-6">THANK YOU!</Title>
        <Paragraph className="text-secondary text-lg mb-6">
            We've received your application for the <Text className="!text-primary">{position?.title}</Text> position.
        </Paragraph>
        <Button type="primary" onClick={onCloseThankYou} className="btn-double-cut">
            CLOSE
        </Button>
    </div>
  );


  return (
    <Modal
      open={isOpen || isThankYouVisible}
      onCancel={isThankYouVisible ? onCloseThankYou : onCancel}
      centered
      width={isThankYouVisible ? 500 : 700}
      title={null}
      footer={
        isThankYouVisible ? null : (
          <div className="flex justify-end">
            <Button key="back" onClick={onCancel} disabled={formProps.isLoading}>
              CANCEL
            </Button>
            <Button
              key="submit"
              type="primary"
              loading={formProps.isLoading}
              onClick={() => formProps.form.submit()}
              className="btn-double-cut"
            >
              {formProps.isLoading ? "SUBMITTING..." : "SUBMIT APPLICATION"}
            </Button>
          </div>
        )
      }
    >
      {isThankYouVisible ? renderThankYouMessage() : renderApplicationForm()}
    </Modal>
  );
};
